import { Component, ElementRef, inject, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { RestStop } from '../../../models/rest-stop';
import { ImageUploadService } from '../../../services/image-upload.service';
import { StateSnackBarService } from '../../../services/state-snack-bar.service';
import { environment } from '../../../../environments/environment';
import { MatButton } from '@angular/material/button';
import { AuthService } from '../../../services/auth.service';
import { NgIf } from '@angular/common';

type Photo = { id: number | string; url: string; createdAt?: string };

@Component({
  selector: 'app-rest-stop-photos',
  standalone: true,
  imports: [MatButton, NgIf],
  templateUrl: './rest-stop-photos.component.html',
  styleUrl: './rest-stop-photos.component.scss',
})
export class RestStopPhotosComponent implements OnChanges, OnDestroy {
  @Input() data!: RestStop;

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('lightboxRoot') lightboxRef?: ElementRef<HTMLDivElement>;

  authService = inject(AuthService);

  latestPhoto?: Photo;
  thumbnails: Photo[] = [];
  allPhotos: Photo[] = [];

  isGalleryOpen = false;
  galleryIndex = 0;

  private touchStartX = 0;
  private touchStartY = 0;
  private touchMoved = false;

  // Portal/Lock Management
  private lightboxPlaceholder: Comment | null = null;
  private scrollY = 0;
  private scrollLocked = false;

  private imageUploadService = inject(ImageUploadService);
  private stateSnackBarService = inject(StateSnackBarService);

  protected readonly environment = environment;

  ngOnChanges(changes: SimpleChanges): void {
    if ('data' in changes) this.computePhotoSets();
  }

  ngOnDestroy(): void {
    // Falls die Komponente während offener Galerie zerstört wird: aufräumen.
    if (this.isGalleryOpen) {
      this.destroyLightboxPortal();
      this.unlockScroll();
      this.isGalleryOpen = false;
    }
  }

  private computePhotoSets(): void {
    const photos: Photo[] = ((this.data?.photos ?? []) as any[]).slice();

    photos.sort((a: any, b: any) => {
      const ta = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      if (tb !== ta) return tb - ta;
      const ia = Number.isFinite(a?.id) ? a.id : parseInt(a?.id ?? '0', 10) || 0;
      const ib = Number.isFinite(b?.id) ? b.id : parseInt(b?.id ?? '0', 10) || 0;
      return ib - ia;
    });

    this.allPhotos = photos;
    this.latestPhoto = photos[0];
    this.thumbnails = photos.slice(1, 7);
  }

  get thumbCols(): number {
    const n = this.thumbnails.length;
    return n >= 3 ? 2 : 1;
  }

  openFilePicker(): void {
    this.fileInput.nativeElement.value = '';
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const el = event.currentTarget as HTMLInputElement;
    const list = el.files;
    if (list && list.length > 0) {
      const file = list[0];
      this.imageUploadService.uploadRestStopPicture(file, this.data.id).subscribe(() => {
        this.stateSnackBarService.success({
          message: 'Dein Bild wurde erfolgreich hochgeladen!',
          subMessage: 'Ein Moderator wird es sich bald ansehen',
        });
      });
    }
  }

  // Galerie
  openGalleryFromIndex(index: number): void {
    if (!this.allPhotos.length) return;
    this.galleryIndex = Math.max(0, Math.min(this.allPhotos.length - 1, index));
    this.isGalleryOpen = true;

    setTimeout(() => {
      const el = this.lightboxRef?.nativeElement;
      if (!el) return;

      // In den Body „portalen“, um Stacking-Context-Probleme zu vermeiden
      if (el.parentElement !== document.body) {
        this.lightboxPlaceholder = document.createComment('lightbox-anchor');
        el.parentElement?.insertBefore(this.lightboxPlaceholder, el);
        document.body.appendChild(el);
      }

      // Scroll sauber sperren
      this.lockScroll();

      // Fokus in die Lightbox
      el.focus();
    }, 0);
  }

  closeGallery(): void {
    this.destroyLightboxPortal();
    this.unlockScroll();
    this.isGalleryOpen = false;
  }

  private destroyLightboxPortal(): void {
    const el = this.lightboxRef?.nativeElement;
    if (el && this.lightboxPlaceholder && this.lightboxPlaceholder.parentNode) {
      this.lightboxPlaceholder.parentNode.insertBefore(el, this.lightboxPlaceholder);
      this.lightboxPlaceholder.parentNode.removeChild(this.lightboxPlaceholder);
      this.lightboxPlaceholder = null;
    } else if (el && el.parentElement === document.body) {
      // Falls kein Placeholder mehr existiert (z. B. Container entmountet), nimm Element raus.
      document.body.removeChild(el);
    }
  }

  private lockScroll(): void {
    if (this.scrollLocked) return;
    this.scrollLocked = true;

    // Position merken und Body fixieren (kein Sprung)
    this.scrollY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  private unlockScroll(): void {
    if (!this.scrollLocked) return;
    this.scrollLocked = false;

    document.documentElement.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';

    // Zur vorherigen Scrollposition zurück
    window.scrollTo(0, this.scrollY);
  }

  prev(): void {
    if (!this.allPhotos.length) return;
    this.galleryIndex = (this.galleryIndex - 1 + this.allPhotos.length) % this.allPhotos.length;
  }
  next(): void {
    if (!this.allPhotos.length) return;
    this.galleryIndex = (this.galleryIndex + 1) % this.allPhotos.length;
  }
  onLightboxKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') this.closeGallery();
    else if (e.key === 'ArrowLeft') this.prev();
    else if (e.key === 'ArrowRight') this.next();
  }

  // Swipe (Mobile)
  onTouchStart(ev: TouchEvent): void {
    if (!ev.touches?.length) return;
    this.touchStartX = ev.touches[0].clientX;
    this.touchStartY = ev.touches[0].clientY;
    this.touchMoved = false;
  }
  onTouchMove(ev: TouchEvent): void {
    if (!ev.touches?.length) return;
    const dx = ev.touches[0].clientX - this.touchStartX;
    const dy = ev.touches[0].clientY - this.touchStartY;
    if (Math.abs(dx) > Math.abs(dy)) {
      this.touchMoved = true;
      ev.preventDefault();
    }
  }
  onTouchEnd(ev: TouchEvent): void {
    if (!this.touchMoved) return;
    const changed = ev.changedTouches?.[0];
    if (!changed) return;
    const dx = changed.clientX - this.touchStartX;
    const threshold = 40;
    if (Math.abs(dx) >= threshold) {
      if (dx < 0) this.next();
      else this.prev();
    }
    this.touchMoved = false;
  }
}
